import { motion } from 'framer-motion'
import { X, ExternalLink, Apple, Leaf, Package } from 'lucide-react'
import { CategoryIcon, unitLabels, statusConfig } from '../../lib/catalogConfig'
import type { Product } from '../../types'
import { useState } from 'react'

interface Props {
  product: Product
  quantity?: number
  onSetQuantity: (qty: number) => void
  onNavigate: (id: string) => void
  onRemove: (id: string) => void
  onClose: () => void
}


export default function FavoriteProductItem({ product, quantity, onSetQuantity, onNavigate, onRemove, onClose }: Props) {
  const [showQtyInput, setShowQtyInput] = useState(false)
  const [inputVal, setInputVal] = useState('')
  const { label, cls } = statusConfig[product.status]

  const confirmQty = () => {
    const n = parseInt(inputVal)
    if (!inputVal.trim() || n <= 0 || isNaN(n)) {
      onSetQuantity(0)  // מחיקת הכמות
    } else {
      onSetQuantity(n)
    }
    setShowQtyInput(false)
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20, height: 0, marginBottom: 0 }}
      transition={{ duration: 0.2 }}
      className="bg-white rounded-2xl border border-[#F2EDE4] overflow-hidden
           shadow-[0_2px_8px_rgba(44,24,16,0.05)] shrink-0"
    >
      <div className="flex items-center gap-3 p-3">
        {/* Thumbnail */}
        <div className="w-18 h-18 lg:w-14 lg:h-14 rounded-xl overflow-hidden flex-shrink-0 bg-[#F2EDE4]">
          {product.images?.[0]
            ? <div className="card-bg-peach h-full">
                <img
                  src={product.images[0]}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  onError={e => { (e.target as HTMLImageElement).style.display = 'none' }}
                />
                <div className="flex items-center justify-center h-full w-full card-bg-green">
                  <CategoryIcon category={product.category} className="w-12 h-12 lg:w-6 lg:h-6 mt-0 text-[#EAE4DB]" />
                </div>
              </div>
            : <div className="w-full h-full flex items-center justify-center text-2xl">
                {product.category === 'fruits' 
                ? <Apple className={cls} />
                : <Leaf  className={cls} />
                }
              </div>
          }
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0 space-y-1.5 lg:space-y-0">
          <p className="text-[16px] lg:text-[14px] font-bold text-[#1A1A1A] truncate">{product.name}</p>
          {(product.custom_sku || product.sku) && (
              <p className="text-[13px] lg:text-[10px] text-[#6E6660]">מק״ט {product.custom_sku ?? product.sku}</p>
            )}
          <div className="flex items-center gap-3.5 lg:gap-2 mt-1">
            <span className="text-[17px] lg:text-[13px] font-semibold text-[#2D6A4F]">
              ₪{product.price}
              <span className="text-[15px] lg:text-[11px] font-normal text-[#6E6660]"> / {unitLabels[product.unit]}</span>
            </span>
            <span className={`text-[12px] lg:text-[10px] font-semibold px-2 py-[3px] lg:px-1.5 lg:py-0.5 rounded-full ${cls}`}>{label}</span>
          </div>
          {!!quantity && quantity > 0 && (
            <p className="text-[11px] text-green-primary font-semibold mt-1">
              כמות: {quantity} {unitLabels[product.unit]}
              </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-1.5">
          <button
            onClick={() => { onNavigate(product.id); onClose() }}
            className="w-8 h-8 lg:w-7 lg:h-7 rounded-lg hover:bg-[#D8F3DC] flex items-center justify-center transition-colors border-none bg-transparent cursor-pointer"
          >
            <ExternalLink className="w-5.5 h-5.5 lg:w-3.5 lg:h-3.5" />
          </button>
          <button
            onClick={() => { setShowQtyInput(v => !v); setInputVal(quantity?.toString() ?? '') }}
            className={`w-8 h-8 lg:w-7 lg:h-7 rounded-lg flex items-center justify-center transition-colors border-none cursor-pointer
              ${quantity ? 'bg-[#D8F3DC] text-green-primary' : 'bg-transparent hover:bg-[#F2EDE4] text-[#A89F97]'}`}
            aria-label="הגדר כמות"
          >
            <Package className="w-5.5 h-5.5 lg:w-3.5 lg:h-3.5 text-[#2D6A4F]" />
          </button>
          <button
            onClick={() => onRemove(product.id)}
            className="w-8 h-8 lg:w-7 lg:h-7 rounded-lg hover:bg-[#FFE8E0] flex items-center justify-center transition-colors border-none bg-transparent cursor-pointer"
          >
            <X className="w-5.5 h-5.5 lg:w-3.5 lg:h-3.5 text-[#C0392B]" />
          </button>
        </div>
      </div>
      {/* Qty input inline */}
      {showQtyInput && (
        <div className="flex items-center gap-2 px-3 pb-3">
          <input
            type="number"
            min={1}
            value={inputVal}
            onChange={e => setInputVal(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') confirmQty() }}
            placeholder="כמות..."
            autoFocus
            className="flex-1 px-3 py-1.5 rounded-[8px] border border-cream-dark text-sm outline-none focus:border-green-primary text-right"
          />
          <button
            onClick={confirmQty}
            className="px-3 py-1.5 rounded-[8px] bg-green-primary text-white text-sm font-medium border-none cursor-pointer hover:opacity-80"
          >
            אישור
          </button>
        </div>
      )}

    </motion.div>
  )
}